import { keepPreviousData, useMutation, useQueryClient } from '@tanstack/react-query';
import { useGrowthQuery } from '@/data/useGrowthQuery';
import { growthKeys } from '@/data/queryKeys';
import { useNotify } from '@/data/notifications';
import { watchJobFailure } from '@/data/jobs';
import {
  ARTICLES_PAGE_SIZE,
  draftArticle,
  fetchArticleDetail,
  fetchArticles,
  handoffArticle,
  patchArticle,
  regenerateArticle,
} from '../api';

export function useArticles(options?: { page?: number; limit?: number; weeks?: number }) {
  const page = options?.page ?? 1;
  const limit = options?.limit ?? ARTICLES_PAGE_SIZE;
  const weeks = options?.weeks ?? 6;

  return useGrowthQuery({
    queryKey: growthKeys.articles.list({ page, limit, weeks }),
    queryFn: () => fetchArticles({ page, limit, weeks }),
    placeholderData: keepPreviousData,
  });
}

export function useArticleDetail(id: string, enabled = true) {
  return useGrowthQuery({
    queryKey: growthKeys.articles.detail(id),
    queryFn: () => fetchArticleDetail(id),
    enabled: enabled && Boolean(id),
  });
}

function jobIdFromResult(result: unknown): string {
  if (!result || typeof result !== 'object') return '';
  const row = result as Record<string, unknown>;
  return String(row.job_id ?? row.jobId ?? '').trim();
}

export function useArticleMutations() {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [...growthKeys.all, 'articles'] });

  const draft = useMutation({
    mutationFn: draftArticle,
    onSuccess: () => {
      notify.success('Article draft created');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const save = useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      title?: string;
      targetKeyword?: string;
      bodyMarkdown?: string;
      meta?: { title?: string; description?: string; slug?: string };
    }) => patchArticle(id, body),
    onSuccess: (_data, vars) => {
      notify.success('Article saved');
      void queryClient.invalidateQueries({ queryKey: growthKeys.articles.detail(vars.id) });
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const regenerate = useMutation({
    mutationFn: regenerateArticle,
    onSuccess: (job) => {
      notify.success('Article regeneration queued');
      void invalidate();
      const jobId = jobIdFromResult(job);
      if (jobId) {
        void watchJobFailure(jobId, (message) => notify.error(message));
      }
    },
    onError: (e) => notify.error(e),
  });

  const handoff = useMutation({
    mutationFn: handoffArticle,
    onSuccess: (job, id) => {
      notify.success('Article handoff queued');
      void queryClient.invalidateQueries({ queryKey: growthKeys.articles.detail(id) });
      void invalidate();
      const jobId = jobIdFromResult(job);
      if (jobId) {
        void watchJobFailure(jobId, (message) => notify.error(message));
      }
    },
    onError: (e) => notify.error(e),
  });

  return { draft, save, regenerate, handoff };
}
